import { css } from '@emotion/react';
import {
  useCallback, useState,
} from 'react';
import {
  ButtonComponent, ButtonStyle,
} from '~/_shared/baseComponents/buttons';
import {
  CheckboxGroupComponent, type CheckboxGroupItem,
} from '~/_shared/baseComponents/checkbox';
import { Stack } from '~/_shared/baseComponents/layout/Stack.componment';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import { BlockWarningComponent } from '~/_shared/baseComponents/warning/blockWarning.component';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { useTranslation } from '~/_shared/utils/hooks';
import type { ModalProps } from '~/modal/modalType.enum';
import { patchFinalizeClientMigration } from '~/store/mapMigration/mapMigration.repository';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';

const sectionHeadingStyle = css({
  fontSize: 16,
  margin: 0,
  fontWeight: 400,
});

const listStyle = css({
  fontSize: 14,
  margin: 0,
});

const checkboxGroupWrapperStyle = css({
  paddingLeft: 20,

  '&& > *': {
    marginTop: 0,
  },
});

const checkboxItems: ReadonlyArray<CheckboxGroupItem<string>> = [
  {
    text: 'I have verified all users have migrated their important data',
    value: 'verifiedUsers',
  },
  {
    text: 'I understand map migrations from Maptive Classic will no longer be possible',
    value: 'noMoreMigrations',
  },
  {
    text: 'I understand this action cannot be undone',
    value: 'noWayBack',
  },
];

type MapMigrationCompleteStepModalContainerProps = Readonly<{
  closeParent: ModalProps['onClose'];
}>;

export const MapMigrationCompleteStepModalContainer = ({
  isOpen,
  onClose,
  closeParent,
}: ModalProps<MapMigrationCompleteStepModalContainerProps>) => {
  const [t] = useTranslation();
  const clientId = useClientIdSelector();
  const [tickedCheckboxes, setTickedCheckboxes] = useState<readonly string[]>([]);
  const [isFinalizingMigration, setIsFinalizingMigration] = useState(false);

  useCallback(async () => {
    setIsFinalizingMigration(true);
    if (clientId) {
      await patchFinalizeClientMigration(clientId);
      onClose();
      closeParent();
    }
    setIsFinalizingMigration(false);
  }, [clientId, closeParent, onClose]);

  const preventContinue = tickedCheckboxes.length < checkboxItems.length;

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      caption="Confirm Migration Completion to Maptive iQ"
      maxWidth={800}
      confirmButton={(
        <TooltipComponent
          tooltipContent={preventContinue ? 'Read through the instructions and acknowledge all the consequences.' : null}
        >
          <ButtonComponent
            buttonStyle={ButtonStyle.Primary}
            onClick={onClose}
            text="Complete Migration"
            isDisabled={preventContinue}
          />
        </TooltipComponent>
      )}
      leftFooterContent={(
        <ButtonComponent
          buttonStyle={ButtonStyle.Secondary}
          onClick={onClose}
          text={t('Cancel')}
        />
      )}
      additionalContent={isFinalizingMigration && (
        <OverlayLoaderComponent />
      )}
    >
      <Stack gap={16}>
        <BlockWarningComponent>
          IMPORTANT: This action will affect all users in your company and cannot be undone
        </BlockWarningComponent>
        <Stack gap={8}>
          <h2 css={sectionHeadingStyle}>
            What will happen after completion:
          </h2>
          <ul css={listStyle}>
            <li>Users can no longer migrate maps from Maptive Classic to Maptive iQ</li>
            <li>All Maptive Classic public map links will redirect to Maptive iQ</li>
            <li>Maptive Classic account changes (passwords, settings) will no longer sync to Maptive iQ</li>
          </ul>
        </Stack>
        <Stack gap={8}>
          <h2 css={sectionHeadingStyle}>
            Confirmation Checklist:
          </h2>
          <Stack gap={12} css={checkboxGroupWrapperStyle}>
            <CheckboxGroupComponent<string>
              items={checkboxItems}
              selectedValues={tickedCheckboxes}
              onSelectionChanged={setTickedCheckboxes}
            />
          </Stack>
        </Stack>
      </Stack>
    </ModalComponent>
  );
};
